import { z, type ZodRawShape, type ZodTypeAny } from 'zod';

export type TFileResponse = {
  data: Blob;
  filename: string;
};

export enum PaginateType {
  CURSOR = 'cursor',
  DEFAULT = 'default',
  NONE = 'none',
}

export type TFormErrorResponse = z.infer<typeof $FormErrorResponse>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const $FormErrorResponse = z.object({
  message: z.string().optional(),
  errors: z.record(z.string(), z.string().array()),
  data: z.object({
    errors: z.record(z.string(), z.string().array()),
  }),
});

export type TPaginationResponse<T> = {
  data: T;
  pagination: TPagination;
};

export type PaginatedResponse<T> = {
  data: T[];
  pagination: {
    count: number;
    currentPage: number;
    links: {
      next: string | null;
      prev: string | null;
    };
    perPage: number;
    total: number;
    totalPages: number;
  };
  status: number;
  success: boolean;
};

export type TCursorPaginationResponse<T> = {
  data: T;
  cursor: TCursorPagination;
};

export const $SortOrder = z.literal('asc').or(z.literal('desc'));
const $SortRequest = z.record(z.string(), $SortOrder);

export const $PaginateType = z.nativeEnum(PaginateType);

const $PageParams = z.object({
  perPage: z.number().optional(),
  page: z.number().optional(),
});

const $CursorParams = z.object({
  perPage: z.number().optional(),
  cursor: z.string().optional(),
  paginate: $PaginateType,
});

const $SortParams = z.object({
  sort: $SortRequest.optional(),
});

const $SearchParams = z.object({
  query: z.string().optional(),
});

export const $DefaultRequestParams = $PageParams
  .merge($SortParams)
  .merge($SearchParams);
const $CursorDefaultRequestParams = $CursorParams
  .merge($SortParams)
  .merge($SearchParams);

export const $Pagination = z.object({
  count: z.number(),
  currentPage: z.number(),
  perPage: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

export const $CursorPagination = z.object({
  count: z.number(),
  current: z.string().nullable(),
  previous: z.string().nullable(),
  next: z.string().nullable(),
});

export const $StringOperator = z
  .literal('or')
  .or(z.literal('and'))
  .or(z.literal('not'));
export const $NumberOperator = z
  .literal('and')
  .or(z.literal('or'))
  .or(z.literal('not'))
  .or(z.literal('range'))
  .or(z.literal('between'))
  .or(z.literal('gt'))
  .or(z.literal('gte'))
  .or(z.literal('lt'))
  .or(z.literal('lte'));
export const $DateOperator = $NumberOperator;
export const $DatetimeOperator = $DateOperator;
export const $SelectOperator = z.literal('and').or(z.literal('not'));
export const $MultiSelectOperator = $StringOperator;

export type TSortOrder = z.infer<typeof $SortOrder>;
export type TDefaultRequestParams = z.infer<typeof $DefaultRequestParams>;
export type TPageParams = z.infer<typeof $PageParams>;
export type TSortParams = z.infer<typeof $SortParams>;
export type TPagination = z.infer<typeof $Pagination>;
export type TCursorParams = z.infer<typeof $CursorParams>;
export type TCursorPagination = z.infer<typeof $CursorPagination>;
export type TPaginateType = z.infer<typeof $PaginateType>;

export const $DefaultMeta = z.object({
  pagination: $Pagination,
});

export const $StringParam = z.object({
  items: z.array(z.string().or(z.null())),
  operator: $StringOperator,
});

export const $NumberParam = z.object({
  items: z.array(z.number()),
  operator: $NumberOperator,
});

export const $DateParam = z.object({
  items: z.array(z.string()),
  operator: $DateOperator,
});

export const $DateTimeParam = z.object({
  items: z.array(z.string()),
  operator: $DatetimeOperator,
});

export const $SelectParam = z.object({
  items: z.array(z.string()),
  operator: $SelectOperator,
});

export const $MultiSelectParam = z.object({
  items: z.array(z.string()),
  operator: $MultiSelectOperator,
});

export type StringParam = z.infer<typeof $StringParam>;
export type NumberParam = z.infer<typeof $NumberParam>;
export type DateParam = z.infer<typeof $DateParam>;
export type DateTimeParam = z.infer<typeof $DateTimeParam>;
export type SelectParam = z.infer<typeof $SelectParam>;
export type MultiSelectParam = z.infer<typeof $MultiSelectParam>;

export type RequestParam =
  | StringParam
  | NumberParam
  | DateParam
  | DateTimeParam
  | SelectParam
  | MultiSelectParam;

export function $getFiltersRequestType<T extends ZodRawShape>(
  data: z.ZodObject<T>
) {
  return z
    .object({ filter: data.partial(), paginate: $PaginateType.optional() })
    .merge($DefaultRequestParams);
}

export function $getPaginationResponseType<T extends ZodTypeAny>(data: T) {
  return z.object({
    data: data.array(),
    pagination: $Pagination,
  });
}

export function $getFiltersCursorRequestType<T extends ZodRawShape>(
  data: z.ZodObject<T>
) {
  return z
    .object({ filter: data.partial() })
    .merge($CursorDefaultRequestParams);
}

export function $getCursorPaginationResponseType<T extends ZodTypeAny>(
  data: T
) {
  return z.object({
    data: data.array(),
    cursor: $CursorPagination,
  });
}

export type DataPicker = Date | null;

export type FilterValue = {
  items: string[];
  operator: string;
};

export type FilterDateValue = {
  items: [DataPicker, DataPicker] | undefined;
  operator: string;
};

export type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

export type SortQuery = Record<string, 'asc' | 'desc'>;
